// ======================================================
// MOVIEFRENZY — WIKIPEDIA / MEDIAWIKI
// Recherche du réalisateur réel d'un film
//
// Phase 1 :
// - trouver l'article Wikipédia du film
// - vérifier le réalisateur dans l'infobox / article
// - retourner une source réelle exploitable
// ======================================================


const SUPPORTED_LANGUAGES = [
    "fr",
    "en",
    "es",
    "de",
];


const normalizeLanguage = (language) => {
    const shortLanguage =
        String(language || "fr")
            .split("-")[0]
            .toLowerCase();

    return SUPPORTED_LANGUAGES.includes(
        shortLanguage
    )
        ? shortLanguage
        : "en";
};


const normalizeText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();


const cleanText = (value) =>
    String(value || "")
        // Références Wikipédia : [1], [12], [1, 2], [a]...
        .replace(
            /\[\s*\d+(?:\s*[-–,]\s*\d+)*\s*\]/g,
            " "
        )
        .replace(
            /\[\s*[a-z]\s*\]/gi,
            " "
        )

        // Nettoyage typographique.
        .replace(/\s+/g, " ")
        .replace(/\s+([,.;:!?])/g, "$1")
        .trim();


const cleanWikipediaNodeText = (node) => {

    if (!node) {
        return "";
    }

    const clone =
        node.cloneNode(true);

    // On retire les éléments Wikipédia qui ne doivent
    // jamais apparaître dans MovieFrenzy.
    clone
        .querySelectorAll(
            [
                "sup.reference",
                ".reference",
                ".mw-ref",
                ".IPA",
                ".API",
                ".prononciation",
                ".pronunciation",
            ].join(", ")
        )
        .forEach(
            (element) =>
                element.remove()
        );

    return cleanText(
        clone.textContent
    );
};

// ======================================================
// GARDE-FOU — CORRESPONDANCE FILM TMDB / WIKIPÉDIA
//
// Empêche un film proche, une suite, un court métrage
// ou une œuvre dérivée d'être pris pour le film courant.
// ======================================================

const normalizeWikipediaMovieTitle = (value) =>
    normalizeText(
        String(value || "")
            // "Dune (film, 2021)" -> "Dune"
            // "Memento (film)" -> "Memento"
            .replace(/\([^)]*\)/g, " ")
    );


const getMovieTitleCandidates = (movie) =>
    [
        ...new Set(
            [
                movie?.title,
                movie?.original_title,
            ]
                .filter(Boolean)
                .map(normalizeWikipediaMovieTitle)
                .filter(Boolean)
        ),
    ];


const scoreWikipediaMovieResult = ({
    result,
    movie,
    year,
}) => {

    const resultTitle =
        normalizeWikipediaMovieTitle(
            result?.title
        );

    const expectedTitles =
        getMovieTitleCandidates(movie);


    if (
        !resultTitle ||
        !expectedTitles.length
    ) {
        return 0;
    }


    let score = 0;


    expectedTitles.forEach(
        (expectedTitle) => {

            // Correspondance parfaite.
            if (
                resultTitle === expectedTitle
            ) {
                score =
                    Math.max(score, 100);

                return;
            }


            // Exemple :
            // titre attendu + précision supplémentaire.
            if (
                resultTitle.startsWith(
                    `${expectedTitle} `
                )
            ) {
                score =
                    Math.max(score, 85);

                return;
            }


            // Le résultat contient entièrement
            // le titre attendu.
            if (
                resultTitle.includes(
                    expectedTitle
                )
            ) {
                score =
                    Math.max(score, 70);

                return;
            }


            // Cas volontairement beaucoup plus faible :
            //
            // "THX 1138"
            // est contenu dans
            // "Electronic Labyrinth: THX 1138 4EB"
            //
            // Ça ne suffit PAS à considérer
            // qu'il s'agit du même film.
            if (
                expectedTitle.includes(
                    resultTitle
                )
            ) {
                score =
                    Math.max(score, 35);
            }
        }
    );


    const normalizedSearchText =
        normalizeText(
            `${result?.title || ""} ${result?.snippet || ""}`
        );

    const filmSignals = [
        "est un film",
        "film américain",
        "film americain",
        "film français",
        "film francais",
        "film britannique",
        "film canadien",
        "is a film",
        "american film",
        "british film",
        "canadian film",
        "es una película",
        "es una pelicula",
        "película estadounidense",
        "pelicula estadounidense",
        "ist ein film",
        "deutscher film",
        "amerikanischer film",
        "spielfilm",
    ];

    const hasFilmSignal =
        filmSignals.some(
            (signal) =>
                normalizedSearchText.includes(
                    normalizeText(signal)
                )
        );

    const hardNonMovieSignals = [
        "est un univers",
        "univers de fiction",
        "univers de science fiction",
        "franchise",

        "is a fictional universe",
        "fictional universe",
        "is a media franchise",
        "media franchise",

        "es un universo",
        "universo ficticio",
        "franquicia",

        "ist ein universum",
        "fiktives universum",
    ];

    const hasHardNonMovieSignal =
        hardNonMovieSignals.some(
            (signal) =>
                normalizedSearchText.includes(
                    normalizeText(signal)
                )
        );

    // ==================================================
    // GARDE-FOU ABSOLU
    //
    // Une page décrivant un univers ou une franchise
    // n'est pas l'article du film recherché.
    // ==================================================

    if (hasHardNonMovieSignal) {
        return 0;
    }

    if (hasFilmSignal) {
        score += 30;
    }

    // Le bon millésime renforce une correspondance,
    // mais ne suffit jamais à sauver un mauvais titre.
    if (
        year &&
        normalizedSearchText.includes(
            String(year)
        )
    ) {
        score += 10;
    }


    return score;
};


// ======================================================
// 1. CHERCHER L'ARTICLE WIKIPÉDIA DU FILM
// ======================================================

const searchWikipediaMoviePage = async ({
    movie,
    directorName,
    language,
}) => {

    const wikiLanguage =
        normalizeLanguage(language);


    const apiUrl =
        `https://${wikiLanguage}.wikipedia.org/w/api.php`;


    const searchTitles =
        [
            ...new Set(
                [
                    movie?.title,
                    movie?.original_title,
                ]
                    .filter(Boolean)
            ),
        ];


    if (!searchTitles.length) {
        return null;
    }


    const year =
        movie?.release_date
            ? movie.release_date.slice(0, 4)
            : "";


    const filmWord = {
        fr: "film",
        en: "film",
        es: "película",
        de: "Film",
    }[wikiLanguage];


    const collectedResults = [];


    // ==================================================
    // On cherche avec le titre localisé ET le titre
    // original lorsqu'ils sont différents.
    // ==================================================

    for (const searchTitle of searchTitles) {

        const searchQuery =
            `${searchTitle} ${year} ${filmWord}`.trim();


        const params =
            new URLSearchParams({
                origin: "*",
                action: "query",
                list: "search",
                srsearch: searchQuery,
                srnamespace: "0",
                srlimit: "5",
                format: "json",
                utf8: "1",
            });


        const response =
            await fetch(
                `${apiUrl}?${params.toString()}`
            );


        if (!response.ok) {
            throw new Error(
                `Wikipedia search HTTP ${response.status}`
            );
        }


        const data =
            await response.json();


        const results =
            data?.query?.search || [];


        results.forEach((result) => {

            collectedResults.push({
                result,
                searchTitle,
                score:
                    scoreWikipediaMovieResult({
                        result,
                        movie,
                        year,
                    }),
            });
        });
    }


    if (!collectedResults.length) {
        return null;
    }


    collectedResults.sort(
        (candidateA, candidateB) =>
            candidateB.score -
            candidateA.score
    );

    // ==================================================
    // VALIDATION RÉELLE DU FILM
    //
    // Le moteur de recherche Wikipédia propose des
    // candidats, mais MovieFrenzy vérifie ensuite
    // l'article lui-même.
    //
    // On exige que le réalisateur recherché apparaisse
    // réellement dans la ligne "Réalisation / Director"
    // de l'infobox.
    //
    // Cela empêche notamment :
    // "Star Wars" (univers / franchise)
    // d'être pris pour le film de 1977.
    // ==================================================

    for (const candidate of collectedResults) {

        if (candidate.score < 70) {
            break;
        }

        const candidateHtml =
            await fetchWikipediaPageHtml({
                apiUrl,
                pageTitle:
                    candidate.result.title,
            });

        if (!candidateHtml) {
            continue;
        }

        const candidateDirectorRow =
            findDirectorRow({
                html: candidateHtml,
                directorName,
                language: wikiLanguage,
            });

        if (!candidateDirectorRow) {
            continue;
        }

        return {
            apiUrl,
            language: wikiLanguage,
            pageTitle:
                candidate.result.title,
            html: candidateHtml,
        };
    }

    console.warn(
        "⚠️ MovieFrenzy Wikipedia: aucun article de film validé",
        {
            movieId: movie?.id,
            movieTitle: movie?.title,
            originalTitle:
                movie?.original_title,
            directorName,
            year,
        }
    );

    return null;
};


// ======================================================
// 2. RÉCUPÉRER LE HTML DE L'ARTICLE
// ======================================================

const fetchWikipediaPageHtml = async ({
    apiUrl,
    pageTitle,
}) => {
    const params =
        new URLSearchParams({
            origin: "*",
            action: "parse",
            page: pageTitle,
            prop: "text",
            format: "json",
            utf8: "1",
        });


    const response =
        await fetch(
            `${apiUrl}?${params.toString()}`
        );


    if (!response.ok) {
        throw new Error(
            `Wikipedia parse HTTP ${response.status}`
        );
    }


    const data =
        await response.json();


    return (
        data?.parse?.text?.["*"] ||
        data?.parse?.text ||
        null
    );
};


// ======================================================
// 3. LIBELLÉS WIKIPÉDIA DU RÉALISATEUR
// ======================================================

const DIRECTOR_LABELS = {
    fr: [
        "réalisation",
        "réalisateur",
        "réalisateurs",
    ],

    en: [
        "directed by",
        "director",
        "directors",
    ],

    es: [
        "dirección",
        "director",
        "directores",
    ],

    de: [
        "regie",
        "regisseur",
        "regisseurin",
    ],
};


// ======================================================
// 4. TROUVER LE RÉALISATEUR DANS L'INFOBOX
// ======================================================

const findDirectorRow = ({
    html,
    directorName,
    language,
}) => {
    if (!html || !directorName) {
        return null;
    }


    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );


    const wikiLanguage =
        normalizeLanguage(language);

    const expectedLabels =
        (
            DIRECTOR_LABELS[wikiLanguage] ||
            DIRECTOR_LABELS.en
        )
            .map(normalizeText);


    const normalizedDirectorName =
        normalizeText(directorName);


    const rows =
        Array.from(
            documentNode.querySelectorAll("tr")
        );


    const matchingRow =
        rows.find((row) => {
            const header =
                row.querySelector("th");

            const value =
                row.querySelector("td");


            if (!header || !value) {
                return false;
            }


            const normalizedHeader =
                normalizeText(
                    header.textContent
                );


            const normalizedValue =
                normalizeText(
                    cleanWikipediaNodeText(value)
                );


            const isDirectorRow =
                expectedLabels.some(
                    (label) =>
                        normalizedHeader === label ||
                        normalizedHeader.startsWith(
                            `${label} `
                        )
                );


            if (!isDirectorRow) {
                return false;
            }


            return normalizedValue.includes(
                normalizedDirectorName
            );
        });


    if (!matchingRow) {
        return null;
    }


    const valueCell =
        matchingRow.querySelector("td");


    return {
        line:
            cleanWikipediaNodeText(
                matchingRow
            ),

        directorText:
            cleanWikipediaNodeText(
                valueCell
            ),
    };
};


// ======================================================
// 5. FALLBACK : CHERCHER LE NOM DANS LE TEXTE
// ======================================================

const findDirectorSentence = ({
    html,
    directorName,
    language,
}) => {
    if (!html || !directorName) {
        return null;
    }


    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );


    const normalizedDirectorName =
        normalizeText(directorName);

    const wikiLanguage =
        normalizeLanguage(language);


    const directingTerms = {
        fr: [
            "réalisé par",
            "réalisée par",
            "réalisateur",
        ],

        en: [
            "directed by",
            "director",
        ],

        es: [
            "dirigida por",
            "dirigido por",
            "director",
        ],

        de: [
            "regie",
            "regisseur",
        ],
    }[wikiLanguage];


    const paragraphs =
        Array.from(
            documentNode.querySelectorAll("p")
        );


    const paragraph =
        paragraphs.find((element) => {
            const text =
                cleanWikipediaNodeText(
                    element
                );

            const normalized =
                normalizeText(text);


            if (
                !normalized.includes(
                    normalizedDirectorName
                )
            ) {
                return false;
            }


            return directingTerms.some(
                (term) =>
                    normalized.includes(
                        normalizeText(term)
                    )
            );
        });


    return paragraph
        ? cleanWikipediaNodeText(paragraph)
        : null;
};


// ======================================================
// 6. CHERCHER UN PASSAGE SUR LE TRAVAIL DE RÉALISATION
// ======================================================

const findDirectorWorkSentence = ({
    html,
    directorName,
    language,
}) => {
    if (!html || !directorName) {
        return null;
    }

    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );

    const wikiLanguage =
        normalizeLanguage(language);

    const normalizedDirectorName =
        normalizeText(directorName);


    // --------------------------------------------------
    // Vocabulaire décrivant réellement un choix
    // ou un travail de réalisation.
    // --------------------------------------------------

    const creativeTerms = {
        fr: [
            "mise en scène",
            "scénario",
            "scénariste",
            "narration",
            "structure narrative",
            "structure",
            "montage",
            "adaptation",
            "photographie",
            "image",
            "rythme",
            "récit",
            "chronologie",
        ],

        en: [
            "direction",
            "screenplay",
            "screenwriter",
            "narrative",
            "narrative structure",
            "structure",
            "editing",
            "adaptation",
            "cinematography",
            "visual",
            "rhythm",
            "story",
            "chronology",
        ],

        es: [
            "dirección",
            "guion",
            "guionista",
            "narrativa",
            "estructura narrativa",
            "estructura",
            "montaje",
            "adaptación",
            "fotografía",
            "imagen",
            "ritmo",
            "relato",
            "cronología",
        ],

        de: [
            "regie",
            "drehbuch",
            "drehbuchautor",
            "erzählung",
            "erzählstruktur",
            "struktur",
            "schnitt",
            "adaption",
            "verfilmung",
            "kamera",
            "bild",
            "rhythmus",
            "handlung",
            "chronologie",
        ],
    }[wikiLanguage];


    // --------------------------------------------------
    // Bruit documentaire :
    // ce sont des indices qu'une phrase parle plutôt
    // des conditions de production que de réalisation.
    // --------------------------------------------------

    const noiseTerms = {
        fr: [
            "pandémie",
            "covid",
            "à distance",
            "festival",
            "sortie du film",
            "date de sortie",
            "box-office",
        ],

        en: [
            "pandemic",
            "covid",
            "remotely",
            "festival",
            "release date",
            "box office",
        ],

        es: [
            "pandemia",
            "covid",
            "a distancia",
            "festival",
            "fecha de estreno",
            "taquilla",
        ],

        de: [
            "pandemie",
            "covid",
            "aus der ferne",
            "festival",
            "erscheinungstermin",
            "kinostart",
            "einspielergebnis",
        ],
    }[wikiLanguage];

    const futureProjectTerms = {
        fr: [
            "en cours d'écriture",
            "en développement",
            "prochain film",
            "film suivant",
            "troisième film",
            "quatrième film",
            "future suite",
            "prochaine suite",
        ],

        en: [
            "being written",
            "in development",
            "next film",
            "future film",
            "third film",
            "fourth film",
            "future sequel",
            "next sequel",
        ],

        es: [
            "en proceso de escritura",
            "en desarrollo",
            "próxima película",
            "película siguiente",
            "tercera película",
            "cuarta película",
            "próxima secuela",
        ],

        de: [
            "wird geschrieben",
            "in entwicklung",
            "nächster film",
            "kommender film",
            "dritter film",
            "vierter film",
            "nächste fortsetzung",
        ],
    }[wikiLanguage];


    const weakCreativeTerms = {
        fr: [
            "scénario",
            "scénariste",
            "adaptation",
        ],

        en: [
            "screenplay",
            "screenwriter",
            "adaptation",
        ],

        es: [
            "guion",
            "guionista",
            "adaptación",
        ],

        de: [
            "drehbuch",
            "drehbuchautor",
            "adaption",
            "verfilmung",
        ],
    }[wikiLanguage];


    const craftActionTerms = {
        fr: [
            "a écrit",
            "écrit",
            "a choisi",
            "choisit",
            "a utilisé",
            "utilise",
            "a tourné",
            "tourne",
            "a filmé",
            "filme",
            "a construit",
            "construit",
            "a conçu",
            "conçoit",
            "a monté",
            "monte",
        ],

        en: [
            "wrote",
            "written",
            "chose",
            "uses",
            "used",
            "shot",
            "filmed",
            "structured",
            "constructed",
            "designed",
            "edited",
        ],

        es: [
            "escribió",
            "escrito",
            "eligió",
            "utilizó",
            "utiliza",
            "rodó",
            "filmó",
            "estructuró",
            "construyó",
            "diseñó",
            "montó",
        ],

        de: [
            "schrieb",
            "geschrieben",
            "wählte",
            "verwendete",
            "verwendet",
            "drehte",
            "filmte",
            "strukturierte",
            "konstruierte",
            "gestaltete",
        ],
    }[wikiLanguage];

    const paragraphs =
        Array.from(
            documentNode.querySelectorAll("p")
        );


    const candidates = [];


    paragraphs.forEach(
        (element, paragraphIndex) => {

            const paragraphText =
                cleanWikipediaNodeText(
                    element
                );

            const normalizedParagraph =
                normalizeText(
                    paragraphText
                );


            // Le paragraphe doit au minimum
            // parler du réalisateur recherché.
            if (
                !normalizedParagraph.includes(
                    normalizedDirectorName
                )
            ) {
                return;
            }


            // --------------------------------------------------
            // IMPORTANT :
            // on découpe maintenant le paragraphe en phrases.
            //
            // Avant :
            // un mot pertinent quelque part dans le paragraphe
            // suffisait à sélectionner TOUT le paragraphe.
            //
            // Maintenant :
            // chaque phrase est évaluée séparément.
            // --------------------------------------------------

            const sentences =
                paragraphText.match(
                    /[^.!?…]+(?:[.!?…]+|$)/g
                ) || [paragraphText];


            sentences.forEach(
                (rawSentence, sentenceIndex) => {

                    const sentence =
                        cleanText(rawSentence);

                    const normalizedSentence =
                        normalizeText(sentence);


                    if (
                        !normalizedSentence.includes(
                            normalizedDirectorName
                        )
                    ) {
                        return;
                    }

                    const isFutureProject =
                        futureProjectTerms.some(
                            (term) =>
                                normalizedSentence.includes(
                                    normalizeText(term)
                                )
                        );


                    if (isFutureProject) {
                        return;
                    }


                    // Trop court = généralement inutile.
                    // Trop long = probablement encore un bloc
                    // encyclopédique trop général.
                    if (
                        sentence.length < 45 ||
                        sentence.length > 260
                    ) {
                        return;
                    }


                    const creativeHits =
                        creativeTerms.filter(
                            (term) =>
                                normalizedSentence.includes(
                                    normalizeText(term)
                                )
                        );


                    // Aucun vocabulaire créatif :
                    // ce n'est pas notre "travail de réalisation".
                    if (!creativeHits.length) {
                        return;
                    }

                    const normalizedWeakCreativeTerms =
                        weakCreativeTerms.map(
                            normalizeText
                        );


                    const onlyWeakCreativeHits =
                        creativeHits.every(
                            (term) =>
                                normalizedWeakCreativeTerms.includes(
                                    normalizeText(term)
                                )
                        );


                    const hasCraftAction =
                        craftActionTerms.some(
                            (term) =>
                                normalizedSentence.includes(
                                    normalizeText(term)
                                )
                        );


                    if (
                        onlyWeakCreativeHits &&
                        !hasCraftAction
                    ) {
                        return;
                    }


                    const noiseHits =
                        noiseTerms.filter(
                            (term) =>
                                normalizedSentence.includes(
                                    normalizeText(term)
                                )
                        );


                    let score =
                        creativeHits.length * 5;


                    // Le nom du réalisateur est présent
                    // directement dans la phrase.
                    score += 3;


                    // On favorise les phrases assez compactes.
                    if (sentence.length <= 180) {
                        score += 2;
                    }


                    // On pénalise fortement le bruit
                    // production / pandémie / sortie / festival.
                    score -=
                        noiseHits.length * 6;


                    candidates.push({
                        sentence,
                        score,
                        creativeHits,
                        noiseHits,
                        paragraphIndex,
                        sentenceIndex,
                    });
                }
            );
        }
    );


    if (!candidates.length) {
        return null;
    }


    candidates.sort(
        (candidateA, candidateB) =>
            candidateB.score -
            candidateA.score
    );


    const bestCandidate =
        candidates[0];


    // Une phrase faiblement pertinente
    // vaut mieux être rejetée que présentée
    // comme une information de réalisation.
    if (bestCandidate.score < 5) {
        return null;
    }


    return bestCandidate.sentence;
};

// ======================================================
// 7. NIVEAU 3 — RECHERCHE PROFONDE DU TRAVAIL
// DU RÉALISATEUR DANS L'ARTICLE
//
// Utilisé uniquement lorsque la recherche normale
// n'a trouvé aucune phrase suffisamment pertinente.
//
// Ici, on explore aussi les sections Production,
// Développement, Tournage, Casting, Montage, etc.
// ======================================================

const findDirectorDeepWorkSentence = ({
    html,
    directorName,
    language,
}) => {
    if (!html || !directorName) {
        return null;
    }


    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );


    const wikiLanguage =
        normalizeLanguage(language);

    const normalizedDirectorName =
        normalizeText(directorName);


    // --------------------------------------------------
    // Sections dans lesquelles une information
    // de réalisation a davantage de valeur.
    // --------------------------------------------------

    const relevantSectionTerms = {
        fr: [
            "production",
            "genèse",
            "développement",
            "tournage",
            "attribution des rôles",
            "casting",
            "réalisation",
            "montage",
            "scénario",
        ],

        en: [
            "production",
            "development",
            "filming",
            "casting",
            "direction",
            "editing",
            "screenplay",
        ],

        es: [
            "producción",
            "desarrollo",
            "rodaje",
            "reparto",
            "casting",
            "dirección",
            "montaje",
            "guion",
        ],

        de: [
            "produktion",
            "entwicklung",
            "dreharbeiten",
            "besetzung",
            "casting",
            "regie",
            "schnitt",
            "drehbuch",
        ],
    }[wikiLanguage] || [];


    // --------------------------------------------------
    // Actions concrètes du réalisateur.
    // Plus exigeant qu'un simple mot comme "réalisateur".
    // --------------------------------------------------

    const directorActionTerms = {
        fr: [
            "a choisi",
            "choisit",
            "a voulu",
            "voulait",
            "a décidé",
            "décide",
            "a utilisé",
            "utilise",
            "a tourné",
            "tourne",
            "a filmé",
            "filme",
            "a coupé",
            "coupe",
            "a monté",
            "monte",
            "a demandé",
            "a encouragé",
            "a privilégié",
            "a opté",
            "envisage de réaliser",
            "a envisagé de réaliser",
            "réaliser une adaptation",
            "a collaboré",
            "collabore",
        ],

        en: [
            "chose",
            "wanted",
            "decided",
            "used",
            "shot",
            "filmed",
            "cut",
            "edited",
            "asked",
            "encouraged",
            "preferred",
            "opted",
            "planned to direct",
            "considered directing",
            "direct an adaptation",
            "collaborated",
        ],

        es: [
            "eligió",
            "quería",
            "decidió",
            "utilizó",
            "rodó",
            "filmó",
            "cortó",
            "montó",
            "pidió",
            "animó",
            "prefirió",
            "optó",
            "planeó dirigir",
            "consideró dirigir",
            "dirigir una adaptación",
            "colaboró",
        ],

        de: [
            "wählte",
            "wollte",
            "entschied",
            "verwendete",
            "drehte",
            "filmte",
            "schnitt",
            "bat",
            "ermutigte",
            "bevorzugte",
            "entschied sich",
            "plante die regie",
            "erwog die regie",
            "eine adaption zu drehen",
            "arbeitete zusammen",
        ],
    }[wikiLanguage] || [];

    // --------------------------------------------------
    // Signaux indiquant plutôt la GENÈSE /
    // le DÉVELOPPEMENT du projet que le travail
    // concret de mise en scène.
    // --------------------------------------------------

    const developmentActionTerms = {
        fr: [
            "envisage de réaliser",
            "a envisagé de réaliser",
            "prévoit de réaliser",
            "réaliser une adaptation",
            "rejoint le projet",
            "a rejoint le projet",
            "développe le projet",
            "a développé le projet",
            "a acquis les droits",
            "acquiert les droits",
        ],

        en: [
            "planned to direct",
            "considered directing",
            "direct an adaptation",
            "joined the project",
            "developed the project",
            "developing the project",
            "acquired the rights",
        ],

        es: [
            "planeó dirigir",
            "consideró dirigir",
            "dirigir una adaptación",
            "se unió al proyecto",
            "desarrolló el proyecto",
            "adquirió los derechos",
        ],

        de: [
            "plante die regie",
            "erwog die regie",
            "eine adaption zu drehen",
            "trat dem projekt bei",
            "entwickelte das projekt",
            "erwarb die rechte",
        ],
    }[wikiLanguage] || [];


    const developmentSectionTerms = {
        fr: [
            "genèse",
            "développement",
        ],

        en: [
            "development",
        ],

        es: [
            "desarrollo",
        ],

        de: [
            "entwicklung",
        ],
    }[wikiLanguage] || [];

    const futureProjectTerms = {
        fr: [
            "en cours d'écriture",
            "en développement",
            "prochain film",
            "film suivant",
            "troisième film",
            "quatrième film",
            "future suite",
            "prochaine suite",
            "suite éventuelle",
            "éventuelle suite",
            "prêt à tourner",
        ],

        en: [
            "being written",
            "in development",
            "next film",
            "future film",
            "third film",
            "fourth film",
            "future sequel",
            "next sequel",
            "possible sequel",
            "ready to shoot",
        ],

        es: [
            "en proceso de escritura",
            "en desarrollo",
            "próxima película",
            "película siguiente",
            "tercera película",
            "cuarta película",
            "próxima secuela",
            "posible secuela",
        ],

        de: [
            "wird geschrieben",
            "in entwicklung",
            "nächster film",
            "kommender film",
            "dritter film",
            "vierter film",
            "nächste fortsetzung",
            "mögliche fortsetzung",
        ],
    }[wikiLanguage] || [];


    // --------------------------------------------------
    // Vocabulaire cinématographique qui renforce
    // la pertinence d'une phrase.
    // --------------------------------------------------

    const craftTerms = {
        fr: [
            "mise en scène",
            "adaptation",
            "tournage",
            "montage",
            "caméra",
            "photographie",
            "scène",
            "séquence",
            "casting",
            "distribution",
            "narration",
            "scénario",
            "improvisation",
            "numérique",
            "pellicule",
        ],

        en: [
            "direction",
            "adaptation",
            "filming",
            "editing",
            "camera",
            "cinematography",
            "scene",
            "sequence",
            "casting",
            "narrative",
            "screenplay",
            "improvisation",
            "digital",
            "film stock",
        ],

        es: [
            "dirección",
            "adaptación",
            "rodaje",
            "montaje",
            "cámara",
            "fotografía",
            "escena",
            "secuencia",
            "casting",
            "reparto",
            "narrativa",
            "guion",
            "improvisación",
            "digital",
        ],

        de: [
            "regie",
            "adaption",
            "verfilmung",
            "dreharbeiten",
            "schnitt",
            "kamera",
            "szene",
            "sequenz",
            "casting",
            "besetzung",
            "erzählung",
            "drehbuch",
            "improvisation",
            "digital",
        ],
    }[wikiLanguage] || [];


    // --------------------------------------------------
    // On parcourt l'article DANS L'ORDRE.
    //
    // Ainsi on sait sous quelle section se trouve
    // chaque paragraphe / élément de liste.
    // --------------------------------------------------

    const nodes =
        Array.from(
            documentNode.querySelectorAll(
                "h2, h3, h4, p, li"
            )
        );


    let currentSection = "";

    const candidates = [];


    nodes.forEach((node, nodeIndex) => {

        // Nouveau titre de section.
        if (
            node.matches("h2, h3, h4")
        ) {
            currentSection =
                cleanWikipediaNodeText(node);

            return;
        }


        const text =
            cleanWikipediaNodeText(node);

        if (!text) {
            return;
        }


        const normalizedText =
            normalizeText(text);


        // Garde-fou très important :
        // la phrase profonde doit tout de même
        // parler explicitement du réalisateur.
        if (
            !normalizedText.includes(
                normalizedDirectorName
            )
        ) {
            return;
        }


        const sentences =
            text.match(
                /[^.!?…]+(?:[.!?…]+|$)/g
            ) || [text];


        sentences.forEach(
            (rawSentence, sentenceIndex) => {

                const sentence =
                    cleanText(rawSentence);

                const normalizedSentence =
                    normalizeText(sentence);


                const isFutureProject =
                    futureProjectTerms.some(
                        (term) =>
                            normalizedSentence.includes(
                                normalizeText(term)
                            )
                    );


                if (isFutureProject) {
                    return;
                }


                if (
                    sentence.length < 45 ||
                    sentence.length > 320
                ) {
                    return;
                }


                // On ne veut toujours pas de :
                // "X est un film réalisé par Y..."
                const genericPatterns = [
                    "est un film",
                    "est une film",
                    "is a film",
                    "is an american film",
                    "es una pelicula",
                    "es un film",
                    "ist ein film",
                    "ist ein amerikanischer film",
                ];


                const isGenericIntroduction =
                    genericPatterns.some(
                        (pattern) =>
                            normalizedSentence.includes(
                                normalizeText(pattern)
                            )
                    );


                if (isGenericIntroduction) {
                    return;
                }


                const actionHits =
                    directorActionTerms.filter(
                        (term) =>
                            normalizedSentence.includes(
                                normalizeText(term)
                            )
                    );


                const craftHits =
                    craftTerms.filter(
                        (term) =>
                            normalizedSentence.includes(
                                normalizeText(term)
                            )
                    );

                const developmentActionHits =
                    developmentActionTerms.filter(
                        (term) =>
                            normalizedSentence.includes(
                                normalizeText(term)
                            )
                    );


                // Pas d'action ET pas de vocabulaire
                // cinématographique : trop faible.
                if (
                    !actionHits.length &&
                    !craftHits.length
                ) {
                    return;
                }


                const normalizedSection =
                    normalizeText(
                        currentSection
                    );

                const isDevelopmentSection =
                    developmentSectionTerms.some(
                        (term) =>
                            normalizedSection.includes(
                                normalizeText(term)
                            )
                    );


                const isRelevantSection =
                    relevantSectionTerms.some(
                        (term) =>
                            normalizedSection.includes(
                                normalizeText(term)
                            )
                    );

                const hasDirectorInSentence =
                    normalizedSentence.includes(
                        normalizedDirectorName
                    );

                let score = 0;


                // Actions concrètes = signal principal.
                score +=
                    actionHits.length * 7;


                // Vocabulaire métier.
                score +=
                    craftHits.length * 4;


                // Production / tournage / développement...
                if (isRelevantSection) {
                    score += 6;
                }


                // Si le réalisateur est explicitement nommé dans
                // cette phrase, c'est un signal particulièrement fort.
                //
                // Mais on autorise également une phrase suivante du
                // même paragraphe : "Il choisit...", "Le réalisateur...",
                // etc., puisque le paragraphe a déjà été validé plus haut.
                if (hasDirectorInSentence) {
                    score += 4;
                }


                // Phrase compacte.
                if (sentence.length <= 220) {
                    score += 2;
                }

                // --------------------------------------------------
                // TYPE DU RÉSULTAT
                //
                // development : genèse / arrivée du réalisateur /
                // développement du projet.
                //
                // craft : choix concret de réalisation,
                // narration, caméra, montage, casting, etc.
                // --------------------------------------------------

                const workType =
                    (
                        developmentActionHits.length > 0 ||
                        (
                            isDevelopmentSection &&
                            actionHits.length > 0 &&
                            craftHits.length <= 1
                        )
                    )
                        ? "development"
                        : "craft";

                candidates.push({
                    sentence,
                    score,
                    section: currentSection,
                    actionHits,
                    craftHits,
                    developmentActionHits,
                    workType,
                    nodeIndex,
                    sentenceIndex,
                });
            }
        );
    });


    if (!candidates.length) {
        return null;
    }


    candidates.sort(
        (candidateA, candidateB) =>
            candidateB.score -
            candidateA.score
    );


    const bestCandidate =
        candidates[0];


    // Niveau 3 :
    // on reste volontairement exigeant.
    if (bestCandidate.score < 10) {
        return null;
    }


    return {
        sentence: bestCandidate.sentence,
        type: bestCandidate.workType,
    };
};

// ======================================================
// API PUBLIQUE MOVIEFRENZY
// ======================================================

export const fetchDirectorMovieRoleFromWikipedia =
    async ({
        movie,
        directorName,
        language = "fr",
    }) => {

        if (!movie?.id || !directorName) {
            return null;
        }


        const page =
            await searchWikipediaMoviePage({
                movie,
                directorName,
                language,
            });


        if (!page) {
            return null;
        }


        const html =
            page.html ||
            await fetchWikipediaPageHtml({
                apiUrl: page.apiUrl,
                pageTitle: page.pageTitle,
            });


        if (!html) {
            return null;
        }


        const directorRow =
            findDirectorRow({
                html,
                directorName,
                language:
                    page.language,
            });


        const directorSentence =
            findDirectorSentence({
                html,
                directorName,
                language:
                    page.language,
            });

        const directorWorkSentenceLevel2 =
            findDirectorWorkSentence({
                html,
                directorName,
                language:
                    page.language,
            });


        const directorWorkLevel3Result =
            !directorWorkSentenceLevel2
                ? findDirectorDeepWorkSentence({
                    html,
                    directorName,
                    language:
                        page.language,
                })
                : null;


        const directorWorkSentenceLevel3 =
            directorWorkLevel3Result?.sentence ||
            null;


        const directorWorkSentence =
            directorWorkSentenceLevel2 ||
            directorWorkSentenceLevel3;

        const directorWorkType =
            directorWorkSentenceLevel2
                ? "craft"
                : directorWorkLevel3Result?.type ||
                null;

        const directorWorkLevel =
            directorWorkSentenceLevel2
                ? 2
                : directorWorkSentenceLevel3
                    ? 3
                    : null;


        const found =
            Boolean(
                directorRow ||
                directorSentence
            );


        const sourceUrl =
            `https://${page.language}.wikipedia.org/wiki/${encodeURIComponent(
                page.pageTitle.replace(
                    / /g,
                    "_"
                )
            )}`;


        return {
            found,

            movieId:
                movie.id,

            movieTitle:
                movie.title,

            directorName,

            pageTitle:
                page.pageTitle,

            language:
                page.language,

            directorLine:
                directorRow?.line ||
                null,

            directorText:
                directorRow?.directorText ||
                null,

            directorSentence,

            directorWorkSentence,

            directorWorkLevel,

            directorWorkType,

            sourceUrl,
        };
    };